
import { Vue, Options } from "vue-class-component";
import { Ref, Watch } from "vue-property-decorator";
import {
  DepartmentApi,
  Employee,
  SystemApi,
  ClientApi,
  EmployeeApi,
  UploadItemEnum,
  GetEmployeeProfile,
  RoleName,
  AuthApi,
  Occupation,
  Position,
  AsExportTypeEnum
} from "../../autogen";
import Page from "../molecules/Page.vue";
import ActionButton from "../atoms/ActionButton.vue";
import Table from "../molecules/Table.vue";
import UIkit from "uikit";
import SearchField from "../atoms/SearchField.vue";
import FileSelectorButton from "../molecules/FileSelectorButton.vue";
import { RouteLocation } from "vue-router";
import EmployeeForm from "../organisms/EmployeeForm.vue";
import Store from "../../store";
import { InstructionLevel } from "../../types";
import Multiselect from "@vueform/multiselect";
import moment from "moment-timezone";
import Datepicker from "vue3-datepicker";
import { ja } from "date-fns/locale";
type FormValues = {
  name?: string;
  occupations?: Occupation[];
  positions?: Position[];
  startDate?: Date;
  endDate?: Date;
};

@Options({
  components: {
    Page,
    ActionButton,
    Table,
    SearchField,
    FileSelectorButton,
    EmployeeForm,
    Multiselect,
    Datepicker
  }
})
export default class Employees extends Vue {
  private readonly storeInstance = Store.getInstance();
  instruction = this.storeInstance.getInstruction();
  employees: GetEmployeeProfile[] = [];
  employee: Employee | {} = {};
  header = [
    { title: "", key: "image", width: 50, type: "avatar" },
    { title: "名前", sortable: true, key: "name", width: 150 },
    { title: "部署", sortable: true, key: "departmentName", width: 150 },
    { title: "職種", key: "occupationName", width: 150 },
    { title: "役職", key: "positionName", width: 150 },
    { title: "最終受検日", key: "last_exam_date", width: 90 },
    {
      title: "受検結果",
      type: "button",
      key: "result",
      width: 120,
      disabled: (row: GetEmployeeProfile) => {
        return !this.appropriateResults || !row.lastExamDate;
      }
    },
    { title: "詳細", type: "button", key: "edit", width: 100, disabled: () => !this.isReadableEmployee }
  ];
  filterColumns = ["name", "departmentName", "occupationName", "positionName", "last_exam_date"];
  @Ref("modal") modal!: HTMLElement;
  formKey = 0;
  availableCreateEmployee = RoleName.ClientAdmin;
  availableRegistration = false;
  appropriateResults = false;
  isReadableEmployee = false;
  isEditableEmployee = false;
  occupations = {};
  positions = {};
  occValues: [] = [];
  posValues: [] = [];
  offset?: string;
  totalCount = "-";
  FormValues: FormValues = { occupations: [], positions: [] };
  locale = ja;
  selectedDepartmentPath: string = "";

  async beforeMount() {
    if (!this.storeInstance.getRole().id) await this.storeInstance.getRoleApi();
    this.availableRegistration = this.$role.isStrongerThan(RoleName.ClientAdmin);
    this.appropriateResults = this.$role.isReadable("employeeStats");
    this.isReadableEmployee = this.$role.isReadable("employee");
    this.isEditableEmployee = this.$role.isEditable("employee");
    const commonCondtions = {
      mode: "tags",
      label: "name",
      trackBy: "name",
      valueProp: "id",
      max: 5,
      closeOnSelect: false,
      searchable: true,
      noResultsText: "候補がありません"
    };
    this.occupations = Object.assign({}, commonCondtions, { options: this.storeInstance.getOccupations() });
    this.positions = Object.assign({}, commonCondtions, { options: this.storeInstance.getPositions() });
    this.setDepartmentPath();
  }
  async getTableData() {
    const departmentId = this.$route.params["departmentId"] as string;
    if (this.offset === null) {
      return [];
    }
    const response = await new DepartmentApi().getEmployees(
      Object.assign({
        limit: "100",
        name: this.FormValues.name ? this.FormValues.name : undefined,
        offset: this.offset ? this.offset : undefined,
        departmentId,
        occupations: this.FormValues.occupations?.length ? this.FormValues.occupations : undefined,
        positions: this.FormValues.positions?.length ? this.FormValues.positions : undefined,
        examStartDate: this.FormValues.startDate ? moment(this.FormValues.startDate).format("YYYY-MM-DD") : undefined,
        examEndDate: this.FormValues.endDate ? moment(this.FormValues.endDate).format("YYYY-MM-DD") : undefined
      })
    );
    this.offset = response.data.offset;
    this.totalCount = response.data.totalCount || "-";
    if (!response.data.lists || !response.data.lists.length) return [];
    return response.data.lists.map(datum =>
      Object.assign(
        {
          name: `${datum.lastName} ${datum.firstName}`,
          departmentName: datum.department?.name || "-",
          occupationName: datum.occupation?.name || "-",
          positionName: datum.position?.name || "-",
          last_exam_date: datum.lastExamDate ? moment(datum.lastExamDate).format("YYYY/MM/DD") : "-"
        },
        datum
      )
    );
  }
  @Watch("$route")
  async onRouteChanged(to: RouteLocation) {
    const path = to.path.split("/").filter(Boolean);
    if (path.slice(-1)[0] === "employees") {
      this.onEmployeeSearch();
      this.setDepartmentPath();
    }
  }
  onAddButtonClick() {
    this.employee = {
      username: "",
      lastName: "",
      firstName: "",
      email: "",
      career: "new",
      departmentId: "",
      occupationId: "",
      positionId: "",
      role: "4",
      contractType: "employee",
      enrollment: "enrolled",
      retirementReasons: [],
      retirementDate: ""
    };
    this.formKey = Date.now();
    this.prepareForm();
  }
  private closeForm() {
    UIkit.modal(this.modal).hide();
  }
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion */
  private prepareForm() {
    UIkit.modal(this.modal).show();
  }
  async onColumnClick(location: { row: number; col: string; el: HTMLElement; rowData: GetEmployeeProfile }) {
    if (location.col === "result") {
      this.$router.push({
        name: "EmployeeStats",
        params: {
          clientId: this.$route.params["clientId"],
          departmentId: this.$route.params["departmentId"],
          peopleId: location.rowData.id
        }
      });
    } else if (location.col === "edit") {
      const response = await new EmployeeApi().getEmployee({
        departmentId: "all",
        employeeId: location.rowData.id
      });
      this.employee = Object.assign({}, response.data, {
        departmentId: response.data.department?.id || "",
        contractType: response.data.contractType?.code || "",
        career: response.data.career?.code || "",
        aditionalDepartmentIds: response.data.additionalDepartments?.length
          ? response.data.additionalDepartments?.map(r => [r.id]).reduce((a, b) => a.concat(b))
          : null,
        anotherDepartmentIds: response.data.anotherDepartments?.length
          ? response.data.anotherDepartments?.map(r => [r.id]).reduce((a, b) => a.concat(b))
          : null,
        occupationId: response.data.occupation?.id || "",
        positionId: response.data.position?.id || ""
      });
      this.prepareForm();
    }
  }
  onEmployeeSearch() {
    const table = this.$refs.tableComponent as Table;
    if (table) {
      this.offset = undefined;
      table.reset();
    }
  }
  async onSubmitted() {
    this.closeForm();
    await new AuthApi().authenticate({}).then(resp => {
      if (resp.status === 200) {
        Store.getInstance().setAuth(resp.data);
      }
    });
    this.onEmployeeSearch();
  }
  async onSelectExcel(file: File) {
    Store.getInstance().setIsLoading(true);
    try {
      await new SystemApi().uploadExcel({
        file,
        item: UploadItemEnum.Employees
      });
      Store.getInstance().setInstructionContent(InstructionLevel.Info, "登録を受け付けました");
      this.onEmployeeSearch();
    } catch (err) {
      let errMessage = "エラーが発生しました";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    } finally {
      Store.getInstance().setIsLoading(false);
    }
  }
  async onResultsExportButtonClick() {
    Store.getInstance().setIsLoading(true);
    const encoding = require("encoding-japanese");
    const departmentId = this.$route.params["departmentId"] as string;
    const response = await new SystemApi().asexport({ type: AsExportTypeEnum.EmployeeWithDept, departmentId });
    const str_array = encoding.stringToCode(response.data);
    const sjis_array = encoding.convert(str_array, "SJIS", "UNICODE");
    const uint8_array = new Uint8Array(sjis_array);

    const url = window.URL.createObjectURL(new Blob([uint8_array], { type: "text/csv;charset=Shift_JIS" }));
    const anchor = document.createElement("a");
    Store.getInstance().setIsLoading(false);
    anchor.href = url;
    anchor.setAttribute("download", `受検結果ダウンロード（従業員）_${new Date().toISOString()}.csv`);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
  async onDownloadTemplateFile(event: Event) {
    event.preventDefault();
    Store.getInstance().setIsLoading(true);
    try {
      const response = await new ClientApi().getXlsTemplate({ peopleType: "employee" }, { responseType: "arraybuffer" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers["content-type"] }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Employees-import-Template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, "エラーが発生しました");
    } finally {
      Store.getInstance().setIsLoading(false);
    }
  }
  resetConditions() {
    this.FormValues = { occupations: [], positions: [] };
    this.onEmployeeSearch();
  }
  setDepartmentPath() {
    const departmentId = this.$route.params["departmentId"] as string;
    this.selectedDepartmentPath = this.storeInstance.getDepartmentPath(departmentId);
  }
}
